import type { ScaleTime } from 'd3';

import { shortMonth } from './date-formatter';
import { tickAnchor } from './tick-anchor';
import { getTextWidth } from './truncate-text';

export type TimeAxisTick = { value: Date; label: string };

const MIN_LABEL_GAP_EM = 0.3;

const buildTicks = (
  scale: ScaleTime<number, number>,
  count: number
): TimeAxisTick[] => {
  const format = scale.tickFormat();
  return scale
    .ticks(count)
    .map((tick) => ({ value: tick, label: shortMonth(format(tick)) }));
};

const labelsFit = (
  ticks: TimeAxisTick[],
  scale: ScaleTime<number, number>,
  fontSize: number
): boolean => {
  const plotWidth = scale.range()[1] ?? 0;
  const minGap = fontSize * MIN_LABEL_GAP_EM;

  const spans = ticks.map((tick, i) => {
    const x = scale(tick.value);
    const width = getTextWidth({ text: tick.label, fontSize }) ?? 0;
    const anchor = tickAnchor(i, ticks.length, x, plotWidth);
    if (anchor === 'start') return { left: x, right: x + width };
    if (anchor === 'end') return { left: x - width, right: x };
    return { left: x - width / 2, right: x + width / 2 };
  });

  return spans.every((span, i) => {
    const previous = spans[i - 1];
    return !previous || span.left - previous.right >= minGap;
  });
};

export const getTimeAxisTicks = ({
  scale,
  tickCount,
  fontSize,
}: {
  scale: ScaleTime<number, number>;
  tickCount: number;
  fontSize: number;
}): TimeAxisTick[] => {
  for (let count = tickCount; count > 1; count--) {
    const ticks = buildTicks(scale, count);
    if (labelsFit(ticks, scale, fontSize)) return ticks;
  }
  return buildTicks(scale, 1).slice(0, 1);
};
